import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {isMobile, isTablet} from 'react-device-detect';
import {CaptureCamera, CaptureFrame, StatusBox, useTimeout} from '@chekinapp/ui';
import {HighlightedTitle, useErrorModal, useIVStore} from '@chekin/shared';
import {InvalidQRModal, useQRScanner} from '@chekin/shared/guest-form';
import {PATHS} from 'routes/constants';
import {usePath} from 'hooks';
import {useReservation} from 'context/reservation';
import {PageLayout} from 'components/PageLayout';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {CaptureHints} from '../../components/CaptureHints';

function IVQRScannerView() {
  const navigate = useNavigate();
  const {t} = useTranslation('identity-verification');
  const {t: tDefault} = useTranslation();
  const {getFullPath} = usePath();
  const setPropertyQRScanned = useIVStore(state => state.setPropertyQRScanned);
  const {displayError, ErrorModal} = useErrorModal();
  const {housing} = useReservation();

  const [isReady, setIsReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);

  const {start: navigateTimedOut} = useTimeout(() => {
    navigate(getFullPath(PATHS.verification.main), {replace: true});
  }, 1500);

  const {webcamRef, startScanning} = useQRScanner({
    expectedUrl: housing?.identity_verification_link,
    onSuccess: () => {
      setPropertyQRScanned(true);
      setShowSuccessIcon(true);
      navigateTimedOut();
    },
    onError: () => {
      setIsModalOpen(true);
      startScanning();
    },
  });

  useEffect(() => {
    setIsReady(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleBackToStart = useCallback(() => {
    setIsModalOpen(false);
    navigate(getFullPath(PATHS.guestRegistration), {replace: true});
  }, [navigate, getFullPath]);

  if (!isReady) return null;

  return (
    <PageLayout
      className="iv-qr-scanner-view"
      kicker={tDefault('guest_registration')}
      title={
        <HighlightedTitle i18nKey="identity-verification:scan_the_entrance_code_title" />
      }
      intro={t('scan_the_entrance_code_intro')}
      backLabel={tDefault('back_to_summary')}
      onBack={handleBackToStart}
      mobileTitle={tDefault('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
    >
      <div className="flex flex-col gap-8">
        <StatusBox
          icon={null}
          status="warning"
          title={t('why_were_asking_you_to_scan')}
          text={<Trans i18nKey="why_were_asking_you_to_scan_description" t={t} />}
          className="order-2 max-w-[560px] lg:order-1"
        />

        <div className="order-1 flex flex-col gap-10 lg:order-2">
          <CaptureCamera
            ref={webcamRef}
            variant="document"
            status={showSuccessIcon ? 'success' : 'scanning'}
            statusLabel={showSuccessIcon ? undefined : t('position_qr_code_inside_frame')}
            additionalConstraints={{
              facingMode: isMobile || isTablet ? 'environment' : 'user',
            }}
            onReady={startScanning}
            onError={displayError}
          >
            <CaptureFrame.Overlay
              status="success"
              visible={showSuccessIcon}
              title={t('qr_code_scanned')}
            />
          </CaptureCamera>

          <CaptureHints variant="qr" />
        </div>

        <InvalidQRModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onBackToStart={handleBackToStart}
        />
        <ErrorModal />
      </div>
    </PageLayout>
  );
}

export {IVQRScannerView};
